import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { GitBranch, ArrowLeft, ShieldAlert, UserPlus, Bug } from 'lucide-react';
import { api } from '../utils/api';

import { UsageStats } from '../components/admin/UsageStats';
import { UserFilters } from '../components/admin/UserFilters';
import { BulkActionBar } from '../components/admin/BulkActionBar';
import { UserTable } from '../components/admin/UserTable';
import { BugReportsPanel } from '../components/admin/BugReportsPanel';
import {
  DeleteDialog, PermanentDeleteDialog, ArchiveDialog,
  BulkArchiveDialog, BulkDeleteDialog, BulkRestoreDialog, BulkPermanentDeleteDialog,
  MessageDialog, AutomataDialog, InviteAdminDialog,
} from '../components/admin/AdminDialogs';

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'archived', label: 'Archived' },
  { key: 'deleted', label: 'Deleted' },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [adminSection, setAdminSection] = useState('users');
  const [activeTab, setActiveTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabCounts, setTabCounts] = useState({ active: 0, archived: 0, deleted: 0 });
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Filters
  const [loginFilter, setLoginFilter] = useState('all');
  const [automataFilter, setAutomataFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('not_admin');

  // Dialog state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAutomata, setUserAutomata] = useState([]);
  const [showAutomataDialog, setShowAutomataDialog] = useState(false);
  const [loadingAutomata, setLoadingAutomata] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [userToArchive, setUserToArchive] = useState(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageTargets, setMessageTargets] = useState([]);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [showBulkArchiveDialog, setShowBulkArchiveDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showBulkRestoreDialog, setShowBulkRestoreDialog] = useState(false);
  const [showBulkPermanentDeleteDialog, setShowBulkPermanentDeleteDialog] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    if (user && !isAdmin) navigate('/dashboard');
  }, [user, isAdmin, navigate]);

  // ============== DATA FETCHING ==============

  const fetchTabCounts = useCallback(async () => {
    const counts = {};
    await Promise.all(TABS.map(async (tab) => {
      try {
        const { ok, data } = await api.get(`/admin/users?status=${tab.key}`);
        if (ok) counts[tab.key] = data.length;
      } catch { /* ignore */ }
    }));
    setTabCounts(prev => ({ ...prev, ...counts }));
  }, []);

  const fetchUsers = useCallback(async (status) => {
    setLoading(true);
    try {
      const { ok, data } = await api.get(`/admin/users?status=${status}`);
      if (ok) {
        setUsers(data);
        setTabCounts(prev => ({ ...prev, [status]: data.length }));
      } else {
        toast.error('Failed to fetch users');
      }
    } catch {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { ok, data } = await api.get('/admin/stats');
      if (ok) setStats(data);
    } catch { /* ignore */ }
  }, []);

  const refreshAll = useCallback(() => {
    fetchUsers(activeTab);
    fetchTabCounts();
    fetchStats();
  }, [activeTab, fetchUsers, fetchTabCounts, fetchStats]);

  useEffect(() => {
    if (isAdmin) refreshAll();
  }, [isAdmin, activeTab, refreshAll]);

  useEffect(() => {
    setLoginFilter('all');
    setAutomataFilter('all');
    setRoleFilter('not_admin');
  }, [activeTab]);

  // ============== SORTING & FILTERING ==============

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'name': return dir * (a.name || '').localeCompare(b.name || '');
        case 'email': return dir * (a.email || '').localeCompare(b.email || '');
        case 'automata_count': return dir * ((a.automata_count || 0) - (b.automata_count || 0));
        case 'created_at': return dir * ((a.created_at || '').localeCompare(b.created_at || ''));
        case 'last_login': return dir * ((a.last_login || '').localeCompare(b.last_login || ''));
        default: return 0;
      }
    });
  }, [users, sortField, sortDir]);

  const filteredUsers = useMemo(() => {
    const now = Date.now();
    return sortedUsers.filter((u) => {
      if (activeTab === 'active' && roleFilter !== 'all') {
        const isAdminUser = u.role === 'admin';
        if (roleFilter === 'admin' && !isAdminUser) return false;
        if (roleFilter === 'not_admin' && isAdminUser) return false;
      }
      if (loginFilter !== 'all') {
        if (loginFilter === 'never') {
          if (u.last_login) return false;
        } else {
          const days = parseInt(loginFilter);
          if (!u.last_login) return true;
          const lastLogin = new Date(u.last_login).getTime();
          if (now - lastLogin < days * 24 * 60 * 60 * 1000) return false;
        }
      }
      if (automataFilter === 'none' && (u.automata_count || 0) > 0) return false;
      if (automataFilter === 'has' && (u.automata_count || 0) === 0) return false;
      return true;
    });
  }, [sortedUsers, loginFilter, automataFilter, roleFilter, activeTab]);

  const hasActiveFilters = loginFilter !== 'all' || automataFilter !== 'all' || (activeTab === 'active' && roleFilter !== 'not_admin');
  const filteredIncludesAdmins = activeTab === 'active' && roleFilter !== 'not_admin';

  const bulkEligibleUsers = useMemo(() => {
    return filteredUsers.filter(u => u.user_id !== user?.user_id && u.role !== 'admin');
  }, [filteredUsers, user]);

  const showBulkBar = bulkEligibleUsers.length > 0 && (
    hasActiveFilters || activeTab === 'archived' || activeTab === 'deleted'
  );

  const filterDescription = useMemo(() => {
    const parts = [];
    if (activeTab === 'active' && roleFilter === 'not_admin') parts.push('non-admin');
    else if (activeTab === 'active' && roleFilter === 'admin') parts.push('admin');
    if (loginFilter === 'never') parts.push('never logged in');
    else if (loginFilter !== 'all') parts.push(`last login > ${loginFilter} days ago`);
    if (automataFilter === 'none') parts.push('no automata');
    else if (automataFilter === 'has') parts.push('has automata');
    return parts.length > 0 ? parts.join(', ') : '';
  }, [loginFilter, automataFilter, roleFilter, activeTab]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const clearFilters = () => {
    setLoginFilter('all');
    setAutomataFilter('all');
    setRoleFilter('not_admin');
  };

  // ============== ACTION HANDLERS ==============

  const handleAction = async (userId, action, successMsg) => {
    setActionLoading(userId);
    try {
      const { ok, data } = await api.post(`/admin/users/${userId}/${action}`, {});
      if (ok) { toast.success(successMsg); refreshAll(); }
      else toast.error(data.detail || `Failed to ${action} user`);
    } catch { toast.error(`Failed to ${action} user`); }
    finally { setActionLoading(null); }
  };

  const handleViewAutomata = async (targetUser) => {
    setSelectedUser(targetUser);
    setLoadingAutomata(true);
    setShowAutomataDialog(true);
    try {
      const { ok, data } = await api.get(`/admin/users/${targetUser.user_id}/automata`);
      if (ok) setUserAutomata(data);
      else toast.error('Failed to fetch automata');
    } catch { toast.error('Failed to fetch automata'); }
    finally { setLoadingAutomata(false); }
  };

  const handleExport = async (u) => {
    try {
      const { ok, data } = await api.get(`/admin/users/${u.user_id}/export`);
      if (ok) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${u.name || u.email}_automatalab_export.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Export downloaded');
      } else toast.error('Failed to export');
    } catch { toast.error('Failed to export'); }
  };

  const handleImpersonate = async (u) => {
    try {
      const { ok, data } = await api.post(`/admin/users/${u.user_id}/impersonate`, {});
      if (ok) {
        const adminToken = localStorage.getItem('session_token');
        localStorage.setItem('admin_token', adminToken);
        localStorage.setItem('session_token', data.session_token);
        localStorage.setItem('impersonating', JSON.stringify({ name: data.user_name, email: data.user_email }));
        window.location.href = '/dashboard';
      } else toast.error(data.detail || 'Failed to impersonate');
    } catch { toast.error('Failed to impersonate'); }
  };

  const handleAdminResetPassword = async (u) => {
    setActionLoading(u.user_id);
    try {
      const { ok, data } = await api.post(`/admin/users/${u.user_id}/admin-reset-password`, {});
      if (ok) toast.success(`Password reset email sent to ${u.email}`);
      else toast.error(data.detail || 'Failed to send reset email');
    } catch { toast.error('Failed to send reset email'); }
    finally { setActionLoading(null); }
  };

  const handleChangeRole = async (u) => {
    const newRole = u.role === 'admin' ? 'user' : 'admin';
    setActionLoading(u.user_id);
    try {
      const { ok, data } = await api.patch(`/admin/users/${u.user_id}/role`, { role: newRole });
      if (ok) { toast.success(`${u.name} is now ${newRole === 'admin' ? 'an admin' : 'a regular user'}`); fetchUsers(activeTab); }
      else toast.error(data.detail || 'Failed to change role');
    } catch { toast.error('Failed to change role'); }
    finally { setActionLoading(null); }
  };

  const openMessageDialog = (targets) => {
    const arr = Array.isArray(targets) ? targets : [targets];
    setMessageTargets(arr);
    setMessageSubject('');
    setMessageBody('');
    setShowMessageDialog(true);
  };

  const handleSendMessage = async () => {
    if (!messageSubject.trim() || !messageBody.trim()) { toast.error('Subject and message are required'); return; }
    setMessageSending(true);
    try {
      if (messageTargets.length === 1) {
        const { ok, data } = await api.post(`/admin/users/${messageTargets[0].user_id}/send-message`, { subject: messageSubject, body: messageBody });
        if (ok) { toast.success(`Email sent to ${messageTargets[0].email}`); setShowMessageDialog(false); }
        else toast.error(data.detail || 'Failed to send email');
      } else {
        const ids = messageTargets.map(u => u.user_id);
        const { ok, data } = await api.post('/admin/bulk/send-message', { user_ids: ids, subject: messageSubject, body: messageBody });
        if (ok) { toast.success(data.message); setShowMessageDialog(false); }
        else toast.error(data.detail || 'Failed to send emails');
      }
    } catch { toast.error('Failed to send email'); }
    finally { setMessageSending(false); }
  };

  const confirmArchive = (u) => { setUserToArchive(u); setShowArchiveDialog(true); };

  const handleArchive = async (notify) => {
    if (!userToArchive) return;
    setShowArchiveDialog(false);
    setActionLoading(userToArchive.user_id);
    try {
      const { ok, data } = await api.post(`/admin/users/${userToArchive.user_id}/archive`, { notify });
      if (ok) { toast.success(notify ? 'User archived and notified' : 'User archived'); refreshAll(); }
      else toast.error(data.detail || 'Failed to archive user');
    } catch { toast.error('Failed to archive user'); }
    finally { setActionLoading(null); setUserToArchive(null); }
  };

  const confirmDelete = (u) => { setUserToDelete(u); setShowDeleteDialog(true); };
  const confirmPermanentDelete = (u) => { setUserToDelete(u); setShowPermanentDeleteDialog(true); };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setShowDeleteDialog(false);
    await handleAction(userToDelete.user_id, 'delete', 'User moved to deleted (30-day retention)');
    setUserToDelete(null);
  };

  const handlePermanentDeleteConfirm = async () => {
    if (!userToDelete) return;
    setShowPermanentDeleteDialog(false);
    await handleAction(userToDelete.user_id, 'permanent-delete', 'User permanently deleted');
    setUserToDelete(null);
  };

  // Bulk actions
  const makeBulkHandler = (endpoint, successCallback) => async (extra) => {
    setBulkLoading(true);
    try {
      const ids = bulkEligibleUsers.map(u => u.user_id);
      const payload = typeof extra === 'boolean' ? { user_ids: ids, notify: extra } : { user_ids: ids };
      const { ok, data } = await api.post(`/admin/bulk/${endpoint}`, payload);
      if (ok) { toast.success(data.message); refreshAll(); }
      else toast.error(data.detail || `Bulk ${endpoint} failed`);
    } catch { toast.error(`Bulk ${endpoint} failed`); }
    finally { setBulkLoading(false); successCallback(); }
  };

  const handleBulkArchive = makeBulkHandler('archive', () => setShowBulkArchiveDialog(false));
  const handleBulkDelete = makeBulkHandler('delete', () => setShowBulkDeleteDialog(false));
  const handleBulkRestore = makeBulkHandler('restore', () => setShowBulkRestoreDialog(false));
  const handleBulkPermanentDelete = makeBulkHandler('permanent-delete', () => setShowBulkPermanentDeleteDialog(false));

  const handleInviteAdmin = async () => {
    if (!inviteEmail.trim()) { toast.error('Email is required'); return; }
    setInviteLoading(true);
    try {
      const { ok, data } = await api.post('/admin/invite', { email: inviteEmail.trim() });
      if (ok) {
        toast.success(data.message);
        setShowInviteDialog(false);
        setInviteEmail('');
        if (data.action === 'promoted') { fetchUsers(activeTab); fetchStats(); }
      } else toast.error(data.detail || 'Failed to send invite');
    } catch { toast.error('Failed to send invite'); }
    finally { setInviteLoading(false); }
  };

  if (!user || !isAdmin) return null;

  // ============== RENDER ==============

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <header className="h-14 border-b border-border flex items-center px-4">
        <div className="flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <GitBranch className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold">AutomataLab</span>
            <span className="text-muted-foreground">/</span>
            <span>Admin</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        <UsageStats stats={stats} />

        {stats && stats.admin_count < 2 && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="admin-warning-banner">
            <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">You're the only admin</p>
              <p className="text-xs text-muted-foreground">Add a backup admin to prevent being locked out of management features.</p>
            </div>
            <Button size="sm" className="h-8 text-xs shrink-0" onClick={() => setShowInviteDialog(true)} data-testid="add-admin-btn">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add Admin
            </Button>
          </div>
        )}

        {/* Section Switcher */}
        <div className="flex gap-2" data-testid="admin-section-switcher">
          <Button
            variant={adminSection === 'users' ? 'default' : 'outline'} size="sm"
            onClick={() => setAdminSection('users')} data-testid="section-users-btn"
          >
            Users
          </Button>
          <Button
            variant={adminSection === 'reports' ? 'default' : 'outline'} size="sm"
            onClick={() => setAdminSection('reports')} data-testid="section-reports-btn"
          >
            <Bug className="w-4 h-4 mr-1.5" /> Bug Reports
          </Button>
        </div>

        {adminSection === 'reports' ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-primary" /> Bug Reports
              </CardTitle>
              <CardDescription>View and manage user-submitted bug reports</CardDescription>
            </CardHeader>
            <CardContent>
              <BugReportsPanel />
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardHeader className="pb-0">
            <CardTitle>Users</CardTitle>
            <CardDescription>View and manage all registered users</CardDescription>
            <div className="flex gap-1 pt-3 border-b border-border -mx-6 px-6">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeTab === tab.key
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  {tab.label}
                  <span className={`ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium ${
                    activeTab === tab.key ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                  }`} data-testid={`tab-count-${tab.key}`}>
                    {tabCounts[tab.key]}
                  </span>
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            <UserFilters
              loginFilter={loginFilter} setLoginFilter={setLoginFilter}
              automataFilter={automataFilter} setAutomataFilter={setAutomataFilter}
              roleFilter={roleFilter} setRoleFilter={setRoleFilter}
              activeTab={activeTab}
              hasActiveFilters={hasActiveFilters} clearFilters={clearFilters}
              filteredCount={filteredUsers.length} totalCount={users.length}
            />

            {showBulkBar && (
              <BulkActionBar
                activeTab={activeTab}
                bulkEligibleCount={bulkEligibleUsers.length}
                filteredCount={filteredUsers.length}
                filteredIncludesAdmins={filteredIncludesAdmins}
                onArchive={() => setShowBulkArchiveDialog(true)}
                onDelete={() => setShowBulkDeleteDialog(true)}
                onRestore={() => setShowBulkRestoreDialog(true)}
                onPermanentDelete={() => setShowBulkPermanentDeleteDialog(true)}
                onEmail={() => openMessageDialog(filteredUsers)}
              />
            )}

            <UserTable
              users={filteredUsers} loading={loading} activeTab={activeTab}
              hasActiveFilters={hasActiveFilters}
              sortField={sortField} sortDir={sortDir} toggleSort={toggleSort}
              currentUserId={user?.user_id}
              onViewAutomata={handleViewAutomata}
              onExport={handleExport}
              onImpersonate={handleImpersonate}
              onMessage={openMessageDialog}
              onResetPassword={handleAdminResetPassword}
              onChangeRole={handleChangeRole}
              onArchive={confirmArchive}
              onDelete={confirmDelete}
              onRestore={(u) => handleAction(u.user_id, 'restore', 'User restored')}
              onPermanentDelete={confirmPermanentDelete}
            />
          </CardContent>
        </Card>
        )}
      </main>

      {/* Dialogs */}
      <DeleteDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog} user={userToDelete} onConfirm={handleDeleteConfirm} />
      <PermanentDeleteDialog open={showPermanentDeleteDialog} onOpenChange={setShowPermanentDeleteDialog} user={userToDelete} onConfirm={handlePermanentDeleteConfirm} />
      <ArchiveDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog} user={userToArchive} onArchive={handleArchive} />
      <BulkArchiveDialog open={showBulkArchiveDialog} onOpenChange={setShowBulkArchiveDialog} count={bulkEligibleUsers.length} filterDescription={filterDescription} loading={bulkLoading} onArchive={handleBulkArchive} />
      <BulkDeleteDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog} count={bulkEligibleUsers.length} filterDescription={filterDescription} loading={bulkLoading} onConfirm={handleBulkDelete} />
      <BulkRestoreDialog open={showBulkRestoreDialog} onOpenChange={setShowBulkRestoreDialog} count={bulkEligibleUsers.length} filterDescription={filterDescription} loading={bulkLoading} onConfirm={handleBulkRestore} />
      <BulkPermanentDeleteDialog open={showBulkPermanentDeleteDialog} onOpenChange={setShowBulkPermanentDeleteDialog} count={bulkEligibleUsers.length} filterDescription={filterDescription} loading={bulkLoading} onConfirm={handleBulkPermanentDelete} />
      <MessageDialog open={showMessageDialog} onOpenChange={setShowMessageDialog} targets={messageTargets} subject={messageSubject} setSubject={setMessageSubject} body={messageBody} setBody={setMessageBody} sending={messageSending} onSend={handleSendMessage} />
      <AutomataDialog open={showAutomataDialog} onOpenChange={setShowAutomataDialog} user={selectedUser} automata={userAutomata} loading={loadingAutomata} />
      <InviteAdminDialog open={showInviteDialog} onOpenChange={setShowInviteDialog} email={inviteEmail} setEmail={setInviteEmail} loading={inviteLoading} onInvite={handleInviteAdmin} />
    </div>
  );
}
